import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, Loader2 } from 'lucide-react';
import { certificateService } from '@/services/certificate/certificate.service';
import { useResizeObserver } from '@/components/dashboard/widgets/cct/use-resize-observer';
import { Button } from '@/components/ui/button';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface CertificatePdfPreviewProps {
  verificationCode: string;
  certificateNumber: string;
}

const CertificatePdfPreview: React.FC<CertificatePdfPreviewProps> = ({ verificationCode, certificateNumber }) => {
  const { t } = useTranslation();
  const { ref, dimensions } = useResizeObserver<HTMLDivElement>();
  const [file, setFile] = useState<Blob | null>(null);
  const [failed, setFailed] = useState(false);
  const [numPages, setNumPages] = useState(0);

  // Fetched once as a blob so the same bytes serve the preview and the download.
  useEffect(() => {
    let cancelled = false;
    setFile(null);
    setFailed(false);
    certificateService
      .getVerifiedCertificateDocument(verificationCode)
      .then((blob) => !cancelled && setFile(blob))
      .catch((err) => {
        console.error('[CertificatePdfPreview] Failed to load certificate document', err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [verificationCode]);

  const handleDownload = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificate-${certificateNumber.replace(/[^\w-]+/g, '-')}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const errorMessage = (
    <p className="py-8 text-center text-sm text-red-600">{t('certificate.verification.failedToLoadDocument')}</p>
  );
  const loadingMessage = (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin" />
      {t('certificate.verification.loadingDocument')}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900">{t('certificate.verification.certificateDocument')}</h2>
        <Button variant="outline" size="sm" onClick={handleDownload} disabled={!file}>
          <Download className="h-4 w-4" />
          {t('certificate.verification.downloadPdf')}
        </Button>
      </div>

      <div ref={ref} className="overflow-hidden rounded border border-gray-200 bg-gray-50">
        {failed ? (
          errorMessage
        ) : !file ? (
          loadingMessage
        ) : (
          <Document
            file={file}
            onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
            loading={loadingMessage}
            error={errorMessage}
          >
            {dimensions.width > 0 &&
              Array.from({ length: numPages }, (_, index) => (
                <Page
                  key={index}
                  pageNumber={index + 1}
                  width={dimensions.width}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              ))}
          </Document>
        )}
      </div>
    </div>
  );
};

export default CertificatePdfPreview;
